import { useMemo, useState, type ReactNode } from 'react';
import type { Segment } from '@shared/types.ts';
import { airlineName, flightAirline } from '@shared/airlines.ts';
import { bagsText, shareLine } from '@shared/itinerary.ts';
import { connectionsOf, layoverText, needsCare } from '@shared/journeys.ts';
import { formatClock, formatDay, formatShortDate, zoneLabel } from '@shared/time.ts';
import { getStore, useAppState, useSegments } from '../../data/store.ts';
import { Icon } from '../../components/Icon.tsx';
import { Link, back, navigate } from '../../lib/router.tsx';
import { copyText } from '../../lib/hooks.ts';
import { flightDuration, mapsUrl, TYPE_LABEL } from './segments.ts';
import { detailFieldId, focusFieldOnOpen } from './SegmentForm.tsx';
import { WhoChips, WhoPicker } from './Who.tsx';

/** One fact about the booking. */
function Row({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="kv-row">
      <span className="k">{k}</span>
      <span className="v">{children}</span>
    </div>
  );
}

/** A small copy button for one value on the page. */
function CopyButton({ text, label, done }: { text: string; label: string; done: string }) {
  const store = getStore();
  return (
    <button
      type="button"
      className="icon-btn btn-soft"
      style={{ width: 36, height: 36 }}
      aria-label={label}
      onClick={async () => store.toast((await copyText(text)) ? done : 'Copy failed')}
    >
      <Icon name="copy" size={16} />
    </button>
  );
}

/** A fact we don't have yet: tapping opens Edit at that field. */
function MissingRow({ k, editUrl, field, notThere }: { k: string; editUrl: string; field: string; notThere: string }) {
  return (
    <Link to={editUrl} className="kv-row" style={{ color: 'var(--text)' }} onClick={() => focusFieldOnOpen(field)}>
      <span className="k">{k}</span>
      <span className="v muted">
        {notThere} · <span style={{ color: 'var(--gold)' }}>Add</span>
      </span>
    </Link>
  );
}

function flightName(s: Segment): string {
  return [s.number, s.from?.code && s.to?.code ? `${s.from.code} → ${s.to.code}` : ''].filter(Boolean).join(' ');
}

export function SegmentScreen({ tripId, segmentId }: { tripId: string; segmentId: string }) {
  const store = getStore();
  const segments = useSegments();
  const changes = useAppState((s) => s.changes);
  const emails = useAppState((s) => s.emails);
  const people = useAppState((s) => s.people);
  const homeCodes = useAppState((s) => s.settings.homeCodes);
  const seg = segments.find((s) => s.id === segmentId);
  const history = useMemo(() => changes.filter((c) => c.segmentId === segmentId).sort((a, b) => b.at.localeCompare(a.at)), [changes, segmentId]);
  const connections = useMemo(() => connectionsOf(segments, homeCodes), [segments, homeCodes]);
  const [pickingWho, setPickingWho] = useState(false);

  if (!seg) {
    return (
      <div className="screen">
        <button type="button" className="back-link" onClick={() => back('/trips')}>
          <Icon name="chevron-left" size={18} /> Travel
        </button>
        <div className="empty">This booking no longer exists.</div>
      </div>
    );
  }

  const source = seg.source.emailId ? emails.find((e) => e.id === seg.source.emailId) : undefined;
  const forwarder = source?.forwardedBy ? people.find((p) => p.id === source.forwardedBy) : undefined;
  const { parsedBy } = seg.source;
  const notThere = parsedBy === 'manual' ? 'Not added' : parsedBy === 'import' ? 'Not in the history' : 'Not in the email';
  const added = formatShortDate(seg.createdAt.slice(0, 10));
  const isTransit = seg.type === 'flight' || seg.type === 'train';
  const isFlight = seg.type === 'flight';
  const editUrl = `/trips/${tripId}/${segmentId}/edit`;
  const missing = (k: string, field: string) => <MissingRow k={k} field={field} editUrl={editUrl} notThere={notThere} />;
  const before = connections.find((c) => c.to.id === seg.id);
  const after = connections.find((c) => c.from.id === seg.id);
  const operatedBy = seg.details['Operated by'];
  const otherDetails = Object.entries(seg.details).filter(([k]) => k !== 'Operated by');
  const bags = bagsText(seg.bags);

  const title = isTransit
    ? `${seg.from?.city ?? seg.from?.name ?? seg.from?.code ?? '?'} → ${seg.to?.city ?? seg.to?.name ?? seg.to?.code ?? '?'}`
    : seg.place?.name ?? seg.title;
  const sub = [isFlight ? flightAirline(seg) : seg.provider, seg.number, isFlight ? flightDuration(seg) : ''].filter(Boolean).join(' · ');
  const maps = mapsUrl(seg.place);

  const startLabel = { flight: 'Departs', train: 'Departs', stay: 'Check-in', car: 'Pick-up', reservation: 'When', activity: 'Starts' }[seg.type];
  const endLabel = { flight: 'Arrives', train: 'Arrives', stay: 'Check-out', car: 'Drop-off', reservation: 'Ends', activity: 'Ends' }[seg.type];

  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/trips')}>
        <Icon name="chevron-left" size={18} /> Travel
      </button>
      <div className="stack" style={{ gap: 4 }}>
        <span className="section-label" style={{ margin: 0 }}>
          {TYPE_LABEL[seg.type]} · {formatDay(seg.start.date)}
          {seg.status === 'cancelled' ? ' · Cancelled' : ''}
        </span>
        <h1 className="h1 h1-sm" style={seg.status === 'cancelled' ? { textDecoration: 'line-through' } : undefined}>
          {title}
        </h1>
        {sub && <span className="muted" style={{ fontSize: 14 }}>{sub}</span>}
      </div>

      <div className="card grid-2" style={{ gap: 12 }}>
        <div className="stack" style={{ gap: 2 }}>
          <span className="tiny muted">{startLabel}</span>
          <span className="big-time">{seg.start.time ? formatClock(seg.start.time) : formatShortDate(seg.start.date)}</span>
          {isTransit && <span style={{ fontSize: 14, fontWeight: 600 }}>{[seg.from?.code ?? seg.from?.name, seg.from?.terminal ? `Terminal ${seg.from.terminal}` : ''].filter(Boolean).join(' · ')}</span>}
          {seg.start.time && <span className="tiny muted">{isTransit ? zoneLabel(seg.start.timeZone) : formatShortDate(seg.start.date)}</span>}
        </div>
        {seg.end && (
          <div className="stack" style={{ gap: 2 }}>
            <span className="tiny muted">
              {endLabel}
              {seg.end.date !== seg.start.date && isTransit ? ` ${formatDay(seg.end.date)}` : ''}
            </span>
            <span className="big-time">{seg.end.time ? formatClock(seg.end.time) : formatShortDate(seg.end.date)}</span>
            {isTransit && <span style={{ fontSize: 14, fontWeight: 600 }}>{[seg.to?.code ?? seg.to?.name, seg.to?.terminal ? `Terminal ${seg.to.terminal}` : ''].filter(Boolean).join(' · ')}</span>}
            {seg.end.time && <span className="tiny muted">{isTransit ? zoneLabel(seg.end.timeZone) : formatShortDate(seg.end.date)}</span>}
          </div>
        )}
      </div>

      {seg.notes ? (
        <section className="card stack" aria-label="Notes" style={{ gap: 6 }}>
          <div className="row-between">
            <span className="tiny muted" style={{ fontWeight: 600 }}>
              NOTES
            </span>
            <Link to={editUrl} className="small" onClick={() => focusFieldOnOpen('sf-notes')}>
              Edit
            </Link>
          </div>
          <span style={{ whiteSpace: 'pre-wrap' }}>{seg.notes}</span>
        </section>
      ) : (
        <Link to={editUrl} className="card card-link" style={{ padding: '12px 16px' }} onClick={() => focusFieldOnOpen('sf-notes')}>
          <Icon name="edit" size={18} />
          <span className="grow">Add a note</span>
        </Link>
      )}

      <div className="card-flush">
        {seg.confirmation ? (
          <div className="kv-row">
            <span className="k">Confirmation</span>
            <span className="row" style={{ gap: 8 }}>
              <span className="v" style={{ letterSpacing: '0.06em', fontSize: 16 }}>
                {seg.confirmation}
              </span>
              <CopyButton text={seg.confirmation} label="Copy confirmation code" done="Confirmation copied" />
            </span>
          </div>
        ) : (
          missing('Confirmation', 'sf-conf')
        )}
        {isFlight &&
          (seg.number ? (
            <div className="kv-row">
              <span className="k">Flight</span>
              <span className="row" style={{ gap: 8 }}>
                <span className="v">{[flightAirline(seg), seg.number].filter(Boolean).join(' ')}</span>
                {/* Just the number ("AA 8459"): what friends need to look the flight up. */}
                <CopyButton text={seg.number} label={`Copy flight number ${seg.number}`} done={`${seg.number} copied`} />
              </span>
            </div>
          ) : (
            flightAirline(seg) && <Row k="Flight">{flightAirline(seg)}</Row>
          ))}
        {isFlight && operatedBy && <Row k="Operated by">{airlineName(operatedBy)}</Row>}
        <div className="kv-row">
          <span className="k">Who's going</span>
          <span className="row" style={{ gap: 10 }}>
            <WhoChips seg={seg} />
            <button type="button" className="text-button" style={{ minHeight: 36 }} onClick={() => setPickingWho(true)}>
              Change
            </button>
          </span>
        </div>
        {pickingWho && <WhoPicker seg={seg} onClose={() => setPickingWho(false)} />}
        {seg.travelers.length > 0 ? <Row k="Names on booking">{seg.travelers.join(', ')}</Row> : missing('Names on booking', 'sf-trav')}
        {isFlight && (seg.seats?.length ? <Row k="Seats">{seg.seats.join(' · ')}</Row> : missing('Seats', 'sf-seats'))}
        {isTransit && (seg.fare ? <Row k="Fare / cabin">{seg.fare}</Row> : missing('Fare / cabin', 'sf-fare'))}
        {isFlight && (bags ? <Row k="Bags">{bags} (per traveler)</Row> : missing('Bags', 'sf-carry'))}
        {seg.price ? <Row k="Price paid">{seg.price} · whole booking</Row> : missing('Price paid', 'sf-price')}
        {maps && !isTransit && (
          <div className="kv-row">
            <span className="k">{seg.place?.address ? 'Address' : 'Map'}</span>
            <a className="v" href={maps} target="_blank" rel="noopener">
              {seg.place?.address ?? `Open ${seg.place?.name ?? 'in Maps'}`}
            </a>
          </div>
        )}
        {otherDetails.map(([k, v]) =>
          v ? (
            <Row key={k} k={k}>
              {v}
            </Row>
          ) : (
            <MissingRow key={k} k={k} field={detailFieldId(k)} editUrl={editUrl} notThere={notThere} />
          ),
        )}
        {before && (
          <Link to={`/trips/${before.from.tripId}/${before.from.id}`} className="kv-row" style={{ color: 'var(--text)' }}>
            <span className="k">Previous flight</span>
            <span className={`v${needsCare(before) ? ' tight' : ''}`}>
              {flightName(before.from)} · {layoverText(before)}
            </span>
          </Link>
        )}
        {after && (
          <Link to={`/trips/${after.to.tripId}/${after.to.id}`} className="kv-row" style={{ color: 'var(--text)' }}>
            <span className="k">Next flight</span>
            <span className={`v${needsCare(after) ? ' tight' : ''}`}>
              {flightName(after.to)} · {layoverText(after)}
            </span>
          </Link>
        )}
        <Row k="Added">
          {parsedBy === 'manual'
            ? 'By hand'
            : parsedBy === 'import'
              ? `From the flight history, ${added}`
              : forwarder
                ? `${forwarder.initial}, forwarded ${added}`
                : `From email, ${added}`}
        </Row>
      </div>

      {history.length > 0 && (
        <section className="stack">
          <h2 className="h2">History</h2>
          <div className="card-flush" style={{ padding: '4px 16px' }}>
            {history.map((c, i) => (
              <div key={c.id} className="row" style={{ padding: '10px 0', alignItems: 'flex-start', borderBottom: i < history.length - 1 ? '1px solid var(--line-soft)' : 'none' }}>
                <span className={`history-dot${i === 0 ? ' latest' : ''}`} />
                <span className="stack" style={{ gap: 2 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{c.summary}</span>
                  <span className="small muted">{formatShortDate(c.at.slice(0, 10))}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <button
        type="button"
        className="btn"
        onClick={async () => store.toast((await copyText(shareLine(seg))) ? 'Copied as text' : 'Copy failed')}
      >
        <Icon name="copy" size={18} /> Copy as text
      </button>

      <div className="row" style={{ gap: 10 }}>
        <Link to={editUrl} className="btn grow">
          <Icon name="edit" size={18} /> Edit
        </Link>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => {
            store.deleteSegment(seg.id);
            navigate('/trips', { replace: true, transition: 'back' });
          }}
        >
          <Icon name="trash" size={18} /> Delete
        </button>
      </div>
    </div>
  );
}
